import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import React, { useEffect, useRef, useState } from "react";
import { DB, functionsBaseUrl } from "../../../app/config/firebase";
import { Customer } from "../../../app/interfaces/customer";
import { OrganizationData } from "../../../app/interfaces/organizationData";
import { ConfigurePaymentForJustifi } from "../../../app/utils/models/configure/configurePayment";
import { enqueueSnackbar } from "notistack";
import firebase from "firebase/app";
import "firebase/firestore";
import { firelikeId } from "../../../app/utils/models/checkers/firelikeid";



export function JustifiPaymentForm({
  orgData,
  customer,
  total,
  credit,
  refNo,
  fulfilled,
  onClose
}: Props) {

  const theme = useTheme();
  const formRef = useRef<HTMLElement>(null);
  const [checkoutId, setCheckoutId] = useState<string>("");
  const [authToken, setAuthToken] = useState<string>("");
  const [adeliePaymentId, setAdeliePaymentId] = useState<string>("");
  const [processingPayment, setProcessingPayment] = useState<boolean>(false);
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
  React.useState<string>("");

const handleChange = (
  event: SelectChangeEvent<typeof selectedPaymentMethod>
) => {
  setSelectedPaymentMethod(event.target.value as string);
};

useEffect(() => {
  if (!orgData) return;

  const getPaymentMethods = async () => {

    if (!customer?.justifi.subAccount) {
      return;
    }

    const url = functionsBaseUrl + "/justifi";
    const data = {
      orgId: orgData.id,
      action: "listPaymentMethods",
      payload: {
        forCustomer: customer?.id,
      },
    };

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    const json = await response.json();
    setPaymentMethods(json);
  };

  getPaymentMethods();

// eslint-disable-next-line react-hooks/exhaustive-deps
}, [orgData]);

  // MARK: Create Checkout Session  
  // Create checkout session when component mounts
  useEffect(() => {
    const createCheckout = async () => {
      try {
        const url = `${functionsBaseUrl}/justifi`;

        const paymentDocId = firelikeId();
        setAdeliePaymentId(paymentDocId);

        const checkoutData = {
          orgId: orgData.id,
          action: "getCheckoutSession",
          payload: {
            amount: Math.round(total * 100),
            description: `Payment for invoice: ${customer?.customerDisplayName}`,
            paymentMethodGroupId: customer?.justifi?.paymentMethodGroupId,
            origin_url: window.location.hostname === 'localhost' 
              ? 'https://app.rentkit.co' 
              : window.location.origin,
            metadata: {
              adelieCustomerId: customer?.id ?? "-",
              adeliePaymentId: paymentDocId,
              justifiCustomerId: customer?.justifi?.customerAccount ?? "placeholder",
              orgId: orgData.id,
              companyName: orgData.companyName,
            },
          }
        };

        console.log("Creating checkout session:", checkoutData);
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(checkoutData)
        });

        const json = await response.json();
        
        if (!response.ok) {
          throw new Error(json.error?.message || "Failed to create checkout session");
        }

        console.log("Checkout session created:", json);
        setCheckoutId(json.checkoutId);
        setAuthToken(json.authToken);
      } catch (error) {
        console.error("Error creating checkout:", error);
        enqueueSnackbar(
          error instanceof Error ? error.message : "Error setting up payment form", 
          { variant: "error" }
        );
        onClose();
      }
    };

    if (orgData?.id && customer?.id) {
      createCheckout();
    }
  }, [orgData, customer, total, onClose]);

  // MARK: Handle Success
  // Handle successful payment
  const handlePaymentSuccess = async (event: any) => {
    try {
      setProcessingPayment(true);
      console.log("Payment event received:", event.detail);

      // Check payment status from the correct path
      const paymentStatus = event.detail.response.data?.payment_status;
      if (paymentStatus !== 'succeeded') {
        throw new Error(`Payment failed with status: ${paymentStatus}`);
      }

      const adeliePaymentDocId = event.detail.response.data?.checkout.payment.metadata?.adeliePaymentId;

      console.log("Payment successful:", event.detail);

      // Create RentKit payment document
      const paymentDocPayload = ConfigurePaymentForJustifi({
        payload: {
          customer: customer,
          paymentAmt: total,
          creditAmt: credit,
          fulfilled: fulfilled,
          paymentDate: firebase.firestore.Timestamp.now(),
          method: "justifi",
          refNo: refNo || "",
          processorSpecific: {
            justifiPaymentId: event.detail.response.data?.payment_id,
            justifiCheckoutId: event.detail.response.data?.checkout_id,
            originalResponse: event.detail.response,
          }
        },
      });

      paymentDocPayload.orgId = orgData.id;

      console.log("Payment document payload:", paymentDocPayload);

      // Save payment document
      await DB.collection("orgs")
        .doc(orgData.id)
        .collection("payments")
        .doc(adeliePaymentDocId)
        .set(paymentDocPayload);

      enqueueSnackbar("Payment processed successfully", { variant: "success" });

      onClose();
    } catch (error) {
      console.error("Error processing payment:", error);
      enqueueSnackbar(
        error instanceof Error ? error.message : "Error processing payment",
        { variant: "error" }
      );
    } finally {
      setProcessingPayment(false);
    }
  };

  // Handle payment errors
  const handlePaymentError = (event: JustifiErrorEvent) => {
    console.error("Payment error:", event.detail);
    enqueueSnackbar(event.detail.message || "Error processing payment", { variant: "error" });
  };

  // MARK: Listeners
  useEffect(() => {
    const form = formRef.current;
    if (!form || !checkoutId || !authToken) return;

    const handleSubmit = (event: any) => {
      console.log("Payment event received:", event.detail);
      handlePaymentSuccess(event);
    };

    const handleError = (event: any) => {
      console.error("Payment error:", event.detail);
      handlePaymentError(event);
    };

    const handleLoaded = (event: any) => {
      console.log("Justifi form loaded", event);
    };

    // Remove any existing listeners first
    form.removeEventListener("submit-event", handleSubmit);
    form.removeEventListener("error-event", handleError);
    form.removeEventListener("loaded", handleLoaded);

    // Add new listeners
    form.addEventListener("submit-event", handleSubmit);
    form.addEventListener("error-event", handleError);
    form.addEventListener("loaded", handleLoaded);

    return () => {
      form.removeEventListener("submit-event", handleSubmit);
      form.removeEventListener("error-event", handleError);
      form.removeEventListener("loaded", handleLoaded);
    };
  }, [checkoutId, authToken]); // Only depend on these values




   // MARK: Submit Payment W/ Saved Method
   const submitPaymentUsingSavedMethod = async () => {
    // Two paths: New payment method or existing payment method (which indicates existing customer account)

    const url = `${functionsBaseUrl}justifi`;
    setProcessingPayment(true);

    const paymentPayload = {
      orgId: orgData.id,
      action: "createPayment",
      payload: {
        amount: Math.round(total * 100),
        currency: "usd",
        captureStrategy: "automatic",
        email: customer?.email ?? "",
        description: `Payment for invoice: ${customer?.customerDisplayName}`,
        paymentMethod: {
          token: "placeholder",
        },
        metadata: {
          adelieCustomerId: customer?.id ?? "unknown",
          justifiCustomerId: "placeholder",
          // invoiceId: invoice.id,
          // invoiceNumber: invoice.invoiceNumber,
          orgId: orgData.id,
          companyName: orgData.companyName,
          adeliePaymentId: "placeholder",
        },
      },
    };

    if (selectedPaymentMethod) {
      // MARK: Existing Method
      paymentPayload.payload.metadata.justifiCustomerId = customer?.justifi.customerAccount ?? "unknown";
      paymentPayload.payload.paymentMethod.token = selectedPaymentMethod;
    } 

    // Create the RentKit payment document first.
    // 1. Create the RentKit payment document.
    // 2. Get the document id.
    // 3. Add the document id to the payment metadata.
      
      const paymentDocPayload = ConfigurePaymentForJustifi({
        payload: {
          customer: customer,
          paymentAmt: total,
          creditAmt: credit,
          fulfilled: fulfilled,
          paymentDate: new Date(),
          method: "justifi",
          refNo: refNo ? refNo : "",
          processorSpecific: {
            justifiPaymentId: "placeholder",
            justifiCheckoutId: "placeholder",
            originalResponse: "placeholder",
          }
        },
      });

      paymentDocPayload.orgId = orgData.id;

      console.log(JSON.stringify(paymentDocPayload))

      const newDocResult = await DB.collection("orgs").doc(orgData.id).collection("payments").add(paymentDocPayload);
      const docId = newDocResult.id;

    // // Then create the actual Justifi payment.
    paymentPayload.payload.metadata.adeliePaymentId = docId;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(paymentPayload),
      });

      const json = await response.json();
      console.log("Payment response:", json);
      
      onClose();
    } catch (error) {
      console.log("Error creating payment:", error);
      onClose();
    } finally {
      setProcessingPayment(false);
    }
  };





  return (
    <Box sx={{ p: 1 }}>
      <Stack direction="column" spacing={2}>
        {/* Show saved payment methods if they exist */}
        {paymentMethods && paymentMethods.length > 0 && (
          <FormControl sx={{ width: "300px" }}>
            <InputLabel id="payment-method-select-label">
              Payment Method
            </InputLabel>
            <Select
              labelId="payment-method-select-label"
              id="payment-method-select"
              value={selectedPaymentMethod}
              onChange={handleChange}
              label="Payment Method"
            >
              <MenuItem value="">
                <em>Add New Payment Method</em>
              </MenuItem>
              {paymentMethods.map((paymentMethod) => (
                <MenuItem
                  key={paymentMethod.card.id}
                  value={paymentMethod.card.id}
                >
                  {`**** **** **** ${paymentMethod.card.acctLastFour}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Show Pay button if a saved method is selected */}
        {selectedPaymentMethod && orgData.justifiSubAccount && (
            <Stack direction="row" gap={1}>
            <LoadingButton
              loading={processingPayment}
              variant="contained"
              onClick={async () => {
                submitPaymentUsingSavedMethod();
              }}
            >
              Pay
            </LoadingButton>

            <Button
              sx={{ background: theme.palette.success.darker }}
              variant="contained"
              disabled={processingPayment}
              onClick={() => {
                setSelectedPaymentMethod("");
              }}
            >
              Add New Payment Method
            </Button>
          </Stack>
        )}

        {/* Show checkout form for new payment method */}
        {(!selectedPaymentMethod || !paymentMethods.length) && checkoutId && authToken && (
          <justifi-checkout
            ref={formRef}
            style={{
              "--jfi-submit-button-background-color": theme.palette.primary.main,
              "--jfi-submit-button-border-color": theme.palette.primary.main,
              "--jfi-submit-button-color": "white",
              "--jfi-submit-button-color-hover": theme.palette.primary.main,
              "--jfi-submit-button-background-color-hover": "white",
              "--jfi-submit-button-border-color-hover": theme.palette.primary.main,
              "--jfi-radio-button-color": theme.palette.primary.main,
              "--jfi-radio-button-background-color": "white",
              "--jfi-radio-button-color-selected": "white",
              "--jfi-radio-button-background-color-selected": theme.palette.primary.main,
              "--jfi-radio-button-border-color": theme.palette.primary.main,
              "--jfi-radio-button-border-color-selected": theme.palette.primary.main,
            }}
            auth-token={authToken}
            checkout-id={checkoutId}
            disable-bnpl={true}
            hide-card-billing-form={false}
          />
        )}

        {(!selectedPaymentMethod || !paymentMethods.length) && !checkoutId && (
          <Box sx={{ p: 2, textAlign: "center" }}>Loading payment form...</Box>
        )}
      </Stack>
    </Box>
  );
}


type Props = {
  orgData: OrganizationData;
  customer?: Customer;
  total: number;
  credit: number;
  refNo: string;
  fulfilled: any[];
  onClose: () => void;
};

// Add type declarations for Justifi web components
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'justifi-checkout': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        'auth-token': string;
        'checkout-id': string;
        'disable-bank-account'?: boolean;
        'disable-bnpl'?: boolean;
        ref?: React.RefObject<any>;
        style?: React.CSSProperties & {
          '--jfi-submit-button-background-color'?: string;
          '--jfi-submit-button-border-color'?: string;
          '--jfi-submit-button-color'?: string;
          '--jfi-submit-button-color-hover'?: string;
          '--jfi-submit-button-background-color-hover'?: string;
          '--jfi-submit-button-border-color-hover'?: string;
          '--jfi-radio-button-color'?: string;
          '--jfi-radio-button-background-color'?: string;
          '--jfi-radio-button-color-selected'?: string;
          '--jfi-radio-button-background-color-selected'?: string;
          '--jfi-radio-button-border-color'?: string;
          '--jfi-radio-button-border-color-selected'?: string;
        };
      };
    }
  }
}

// Add type for Justifi event responses
interface JustifiSubmitEvent extends CustomEvent {
  detail: {
    response: {
      id: string;
      type: string;
      data: {
        id: string;
        payment_status: string;
        payment_id: string;
        checkout_id: string;
        payment_response: {
          id: string;
          type: string;
          data: {
            id: string;
            status: string;
            metadata: Record<string, string>;
          };
        };
      };
    };
  };
}

interface JustifiErrorEvent extends CustomEvent {
  detail: {
    message: string;
    code?: string;
  };
}